/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Snackbar,
  Backdrop,
  CircularProgress,
  Breadcrumbs,
  Link,
  Tooltip,
  Container,
  Switch
} from "@mui/material";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import "./Plan.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import Alert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";

// Helper functions for navigation
const buildNavigationState = (teamId, sellerId) => ({
  state: { teamId, sellerId },
});

const buildFullNavigationState = (teamId, sellerId, product, usedRecurringFrequencies) => ({
  state: { teamId, sellerId, product, usedRecurringFrequencies },
});

// Helper functions for styles
const getContainerStyle = (submissionDetails) => {
  const isDialogue = submissionDetails?.status === "DRAFT";
  return {
    display: "flex",
    flexDirection: isDialogue ? "column" : "row",
    justifyContent: isDialogue ? "center" : "flex-start",
    width: "1260px",
  };
};

const getSideMenuStyle = (submissionDetails) => ({
  marginTop: submissionDetails?.status === "DRAFT" ? "0px" : "69px",
});

const getSubmissionContainerStyle = (submissionDetails) => ({
  width: submissionDetails?.status === "DRAFT" ? "100%" : "89%",
  marginTop: submissionDetails?.status === "DRAFT" ? "0" : "12px",
  marginLeft: submissionDetails?.status === "DRAFT" ? "0" : "70px",
});

// Submission details check helper
const isSubmissionDetailsValid = (submissionDetails) =>
  submissionDetails && Object.keys(submissionDetails).length > 0;

// Recurring frequency dropdown options
const RECURRING_FREQUENCY_OPTIONS = [
  { key: "1-DAY", value: "Daily" },
  { key: "1-MONTH", value: "Monthly" },
  { key: "3-MONTH", value: "Quarterly" },
  { key: "6-MONTH", value: "Half yearly" },
  { key: "1-YEAR", value: "Yearly" },
];

// Loading backdrop component
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

// Form action buttons component
const FormActionButtons = ({ onCancel, disabled }) => (
  <Stack direction="row" spacing={2}>
    <Button
      variant="contained"
      sx={(theme) => ({
        backgroundColor: "#e8e8e8",
        color: "black",
        textTransform: "none",
      })}
      onClick={onCancel}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      disabled={disabled}
      sx={{ textTransform: "none" }}
      type="submit"
    >
      Save
    </Button>
  </Stack>
);

FormActionButtons.propTypes = {
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Helper to build plan price object
const buildPlanPrice = (obj, productId) => ({
  isTaxable: false,
  periodUnit: "DAY",
  price: Number(obj.price).toFixed(2),
  status: "ACTIVE",
  currencyCode: obj.currencyCode,
  countryCode: obj.countryCode,
  country: obj.country,
  taxRateinPercent: obj.taxRateinPercent,
  taxRate: obj.taxRate,
  taxIncluded: false,
  name: productId + "_" + obj.countryCode,
  description: productId + "_" + obj.countryCode,
});

function RenderEditIcon({ id, unsavedChangesRef, planId }) {
  const apiRef = useGridApiContext();

  return (
    <>
      {unsavedChangesRef.current.unsavedRows !== id && (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <strong>
            {!planId && (
              <EditIcon
                className="edit-icon"
                onClick={() => {
                  apiRef.current.startCellEditMode({
                    id: id,
                    field: "price",
                  });
                  unsavedChangesRef.current.unsavedRows = id;
                }}
              />
            )}
          </strong>
        </div>
      )}
    </>
  );
}

RenderEditIcon.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unsavedChangesRef: PropTypes.shape({
    current: PropTypes.object.isRequired,
  }).isRequired,
  planId: PropTypes.string,
};

function Plan(props) {
  const { appId, productId, planId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const sellerId = location.state.sellerId;
  const product = location.state.product;
  const usedRecurringFrequencies = location.state.usedRecurringFrequencies;
  const [recurringFrequencyVal, setRecurringFrequencyVal] =
    React.useState("1-DAY");
  const [exchangePriceData, setExchangePriceData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  const errorsApp = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [countryTableRows, setCountryTableRows] = useState([]);

  useEffect(() => {
    if (appData?.submissions?.length > 0) {
      dispatch(fetchSubmissionDetails(appId, appData.submissions[0].id));
    }
  }, [appData]);
  useEffect(() => {
    if (planId) {
      APIManager.getPlanDetails(productId, planId).then((json) => {
        setValue("name", json.recurringPlans[0].name);
        setRecurringFrequencyVal(
          json.recurringPlans[0].recurringFrequency +
            "-" +
            json.recurringPlans[0].recurringFrequencyUnit
        );
        setValue("basePrice", json.recurringPlans[0].basePrice.price);
        setValue("gracePeriod", json.recurringPlans[0].gracePeriod);
        setExchangePriceData(json.recurringPlans[0].planPrices);
        createDataGrid(json.recurringPlans[0].planPrices);
      });
    }
    setLoader(false);
  }, []);

  const backToApplications = () => {
    navigate(-1);
  };

  const backToAppDetails = () => {
    navigate(`/ui/developer/applications/${appId}`, 
      buildNavigationState(teamId, sellerId)
    );
  };

  const backToInApp = () => {
    navigate(`/ui/developer/applications/${appId}/app-product`,
      buildNavigationState(teamId, sellerId)
    );
  };

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };
  const populatePrice = (event) => {
    setLoader(true);
    let basePrice = event.target.value;
    if (!Number.isNaN(Number.parseFloat(basePrice)) && Number.parseFloat(basePrice) != 0) {
      APIManager.getExchangeRates(basePrice, "USD").then((json) => {
        if (json.currencyData && json.currencyData.length > 0) {
          setExchangePriceData(json.currencyData);
          createDataGrid(json.currencyData);
          setLoader(false);
        }
      });
    }
    if (basePrice === "") {
      setExchangePriceData([]);
      setCountryTableRows([]);
    }
    setLoader(false);
  };
  const createDataGrid = (data) => {
    let countryData = [];
    let count = 1;
    data.forEach((country) => {
      countryData.push({
        id: count,
        country: country.country,
        currencyCode: country.currencyCode,
        price: country.price,
        taxRate:
          country.taxRateinPercent +
          " (" +
          country.currencyCode +
          " " +
          country.taxRate +
          ")",
      });
      count++;
    });
    setCountryTableRows(countryData);
  };

  const countryTableColumns = [
    {
      field: "country",
      headerName: "Country",
      width: 140,
      headerClassName: "country-table-general-header",
    },
    {
      field: "currencyCode",
      headerName: "Price",
      align: "right",
      headerAlign: "right",
      sortable: false,
      headerClassName: "country-table-general-header",
      renderCell: (params) => (
        <span style={{ color: "#717273" }}>{params.value}</span>
      ),
    },
    {
      field: "price",
      headerName: planId ? "" : "(Editable)",
      editable: !planId,
      align: "left",
      headerAlign: "left",
      type: "number",
      width: 80,
      headerClassName: "price-colum-header",
    },
    {
      field: "editTax",
      headerName: "",
      align: "left",
      renderCell: (params) => (
        <RenderEditIcon id={params.id} unsavedChangesRef={unsavedChangesRef} planId={planId} />
      ),
      sortable: false,
      headerClassName: "country-table-general-header",
    },
    {
      field: "taxRate",
      headerName: "Tax Rate",
      width: 140,
      align: "left",
      headerAlign: "left",
      headerClassName: "country-table-general-header",
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-start' }}>
          {params.value}
        </div>
      ),
    },
  ];

  const unsavedChangesRef = React.useRef({
    unsavedRows: "",
  });

  const onSubmit = (data) => {
    setLoader(true);
    let newPlan = product;
    let planPrices = [];
    let productData = exchangePriceData.filter(
      (item) => item.countryCode !== "USA"
    );
    productData.forEach((obj) => {
      planPrices.push(buildPlanPrice(obj, data.productId));
    });
    let usObject = exchangePriceData.find(
      (item) => item.countryCode === "USA"
    );
    let plan = {
      name: data.name,
      planType: "RECURRING",
      recurringFrequency: data.recurringFrequency.split("-")[0],
      recurringFrequencyUnit: data.recurringFrequency.split("-")[1],
      chargeType: "IMMEDIATE",
      autoRenew: true,
      gracePeriod: data.gracePeriod,
      gracePeriodUnit: "DAY",
      planPrices: [],
      basePrice: {
        productId: product.product.id,
        price: data.basePrice,
        status: "ACTIVE",
        basePrice: true,
        country: usObject.country,
        currencyCode: usObject.currencyCode,
        countryCode: usObject.countryCode,
        taxRateinPercent: usObject.taxRateinPercent,
        taxRate: usObject.taxRate,
        name: data.name + "_" + usObject.currencyCode,
        description: data.name + "_" + usObject.currencyCode,
      },
    };
    plan.planPrices = planPrices;
    newPlan.recurringPlans = [plan];
    savePlan(newPlan);
  };

  const savePlan = (data) => {
    APIManager.savePlanInApp(data).then((json) => {
      setOpenSnackbar(true);
      setSnackMsg(`Added successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, lastUpdated: new Date() };
    let countryObj = exchangePriceData.find(
      (item) => item.country === newRow.country
    );
    let changedPrice = newRow.price;
    let newTaxRate = newRow.price * (countryObj.taxRateinPercent / 100);
    updatedRow.taxRate =
      countryObj.taxRateinPercent +
      " (" +
      countryObj.currencyCode +
      " " +
      newTaxRate.toFixed(2) +
      ")";
    updatedRow.price = Number(changedPrice).toFixed(2);
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).price = updatedRow.price;
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).taxRate = newTaxRate.toFixed(2);
    unsavedChangesRef.current.unsavedRows = "";
    return updatedRow;
  };

  const checkExistingPlanFrequency = (frequency) => {
    return usedRecurringFrequencies.includes(frequency);
  };

  const handleProcessRowUpdateError = (error) => {};
  const handleRecurringFrequencyChange = (event) => {
    setRecurringFrequencyVal(event.target.value);
  };

  return (
    <>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Topheader />
      {
        <Container maxWidth="lg">
                          <Grid container>
                            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
        <div
          className="container"
          style={getContainerStyle(submissionDetails)}
        >
          <div
            className="side-menu"
            style={getSideMenuStyle(submissionDetails)}
          >
            <button
              className="arrow-back"
              onClick={backToApplications}
              onKeyDown={(e) => e.key === "Enter" && backToApplications()}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
              type="button"
            >
              <div>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">Product Overview</div>
            </button>
            {isSubmissionDetailsValid(submissionDetails) &&
              submissionDetails.status !== "DRAFT" &&
              appData &&
              Object.keys(appData).length > 0 && (
                <ApplicationDetailsSideMenu
                  appData={appData}
                  teamId={teamId}
                  isMonetized={submissionDetails.isMonetized}
                  callNewSubmissionId={callNewSubmissionId}
                />
              )}
          </div>
          <div
            className="submission-container"
            style={getSubmissionContainerStyle(submissionDetails)}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="in-app-title" style={{ marginLeft: "15%" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToAppDetails}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {isSubmissionDetailsValid(submissionDetails) && (
                      <>{submissionDetails.descriptions[0].applicationName}</>
                    )}
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToInApp}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    In App Products
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    Edit Product
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    {planId && <>View Plan</>}
                    {!planId && <>Add New Plan</>}
                  </Link>
                </Breadcrumbs>
                <Grid container spacing={2}>
                  {planId && (
                    <Grid size={9}>
                      <h2>View Plan</h2>
                    </Grid>
                  )}
                  {!planId && (
                    <Grid size={9}>
                      <h2>Add New Plan</h2>
                    </Grid>
                  )}
                  <Grid size={3}>
                    <FormActionButtons
                      onCancel={backToApplications}
                      disabled={!isValid || countryTableRows.length === 0}
                    />
                  </Grid>
                </Grid>
              </div>
              <div className="products-container">
                <Grid container spacing={2}>
                  <Grid size={4}>Plan Information</Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="name-input">Name</label>
                      <TextField
                        {...register("name", {
                          required: "Name is required",
                        })}
                        id="name-input"
                        disabled={planId}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Name"
                        variant="outlined"
                      />

                      {errors.name && (
                        <Alert severity="error">{errors.name.message}</Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container-wide">
                <Divider flexItem />
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}>Price Settings</Grid>
                  <Grid size={3}>
                    <div>
                      <label className="form-label required" htmlFor="basePrice-input">
                        Recurring Price
                      </label>
                      <TextField
                        {...register(
                          "basePrice",
                          {
                            onBlur: (e) => {
                              populatePrice(e);
                            },
                          },
                          { required: "Base Price is required." }
                        )}
                        disabled={planId}
                        id="basePrice-input"
                        type="number"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                USD
                              </InputAdornment>
                            ),
                          },
                        }}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        variant="outlined"
                        helperText="Base Price doesn't include tax"
                      />
                      {errors.basePrice && (
                        <Alert severity="error">
                          {errors.basePrice.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  <Grid size={3}>
                    <div>
                      <label className="form-label required" htmlFor="recurringFrequency-select">
                        Recurring Frequency
                      </label>
                      <Select
                        {...register("recurringFrequency", {
                          required: "Recurring Frequency is required.",
                        })}
                        labelId="demo-simple-select-label"
                        fullWidth
                        disabled={planId}
                        id="recurringFrequency-select"
                        value={recurringFrequencyVal}
                        onChange={handleRecurringFrequencyChange}
                      >
                        {RECURRING_FREQUENCY_OPTIONS.map((frequency) => (
                          <MenuItem
                            key={frequency.key}
                            value={frequency.key}
                            disabled={checkExistingPlanFrequency(frequency.key)}
                          >
                            {frequency.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.recurringFrequency && (
                        <Alert severity="error">
                          {errors.recurringFrequency.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  <Grid size={2.5}>
                    <div>
                      <label className="form-label required" htmlFor="chargeTime-select">
                        When To Charge
                      </label>
                      <Select
                        {...register("chargeTime")}
                        labelId="demo-simple-select-label"
                        fullWidth
                        disabled={planId}
                        value=""
                        displayEmpty
                        id="chargeTime-select"
                      >
                        <MenuItem value="">Immediately</MenuItem>
                      </Select>
                      {errors.chargeTime && (
                        <Alert severity="error">
                          {errors.chargeTime.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={3}>
                    <div>
                      <label className="form-label" htmlFor="gracePeriod-input">
                        Grace Period
                        <Tooltip
                          title={
                            "Grace period is given to user in case of payment failure for recurring product"
                          }
                          placement="right-start"
                        >
                          <InfoIcon
                            fontSize={"5px"}
                            sx={{
                              color: "#b8b9b9",
                              cursor: "pointer",
                              marginLeft: "1%",
                              marginBottom: "-1%",
                            }}
                          />
                        </Tooltip>
                      </label>
                      <TextField
                        {...register("gracePeriod", {
                          max: {
                            value: 30,
                            message:
                              "Grace period should be less than 30 days.",
                          },
                          min: {
                            value: 0,
                            message: "Invalid grace period",
                          },
                        })}
                        disabled={planId}
                        id="gracePeriod-input"
                        type="number"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                Days
                              </InputAdornment>
                            ),
                          },
                        }}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        variant="outlined"
                        helperText="Max 30 days"
                      />
                      {errors.gracePeriod && (
                        <Alert severity="error">
                          {errors.gracePeriod.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    {countryTableRows.length > 0 && (
                      <div
                        style={{ height: 300, width: "100%", marginBottom: 60 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                          gutterBottom
                        >
                          Country specific price setting{" "}
                          <span className="red-star">*</span>
                        </Typography>
                        <Typography
                          variant="caption"
                          gutterBottom
                          sx={{ display: "block", color: "#757575" }}
                        >
                          These prices can be manually changed as per how you
                          want to change
                        </Typography>
                        <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                        <DataGrid
                          elevation={0}
                          rows={countryTableRows}
                          columns={countryTableColumns}
                          disableColumnMenu
                          processRowUpdate={processRowUpdate}
                          onProcessRowUpdateError={handleProcessRowUpdateError}
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { page: 0, pageSize: 5 },
                            },
                          }}
                          rowHeight={34}
                          columnHeaderHeight={32}
                          localeText={{
                            MuiTablePagination: {
                              labelRowsPerPage: "Items per page:",
                            },
                          }}
                          sx={{
                            '& .MuiDataGrid-cell': {
                              padding: '4px 8px',
                              display: 'flex',
                              alignItems: 'center',
                              height: '34px',
                              minHeight: '34px',
                            },
                            '& .MuiDataGrid-columnHeader': {
                              padding: '4px 8px',
                              display: 'flex',
                              alignItems: 'center',
                              height: '32px',
                              minHeight: '32px',
                            },
                            '& .MuiDataGrid-row': {
                              minHeight: '34px !important',
                              maxHeight: '34px !important',
                            },
                            '& .MuiDataGrid-cellContent': {
                              display: 'flex',
                              alignItems: 'center',
                              height: '100%',
                            },
                          }}
                        />
                        </Paper>
                      </div>
                    )}
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    <div className="monetize-wrapper">
                      <div className="wrapper-diver">
                        <p
                          className="monetize-text"
                          style={{ fontWeight: "bold" }}
                        >
                          Enable Auto Renew{" "}
                          <span style={{ color: "red" }}>*</span>
                          <br />
                          <span
                            className="monetize-text-small"
                            style={{ marginLeft: "0" }}
                          >
                            <b>Note: </b>
                            <span style={{ color: "#757575" }}>
                              This setting cannot be undone once this product is
                              published
                            </span>
                          </span>
                        </p>

                        <div>
                          <Switch
                            {...register("enableRecurrence")}
                            disabled
                            checked
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </form>
          </div>
        </div>
        </Grid>
                        </Container>
      }
      {errorsApp?.errorMessage != null &&
        errorsApp?.errorType != null && <ApplicationDetailsErrorDialog />}
      {(appData.loading || loading) && <LoadingBackdrop open={loading} />}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
    </>
  );
}

export default Plan;
